import { useEffect, useState } from 'react';

const SWAMI_LOGO = '/swami-logo.png';

export default function SplashScreen() {
  const [fadeOut, setFadeOut] = useState(false);
  const [showText, setShowText] = useState(false);
  const [logoAnimated, setLogoAnimated] = useState(false);

  useEffect(() => {
    // Start logo animation after a small delay
    const logoTimer = setTimeout(() => {
      setLogoAnimated(true);
    }, 100);

    // Show text after logo animation
    const textTimer = setTimeout(() => {
      setShowText(true);
    }, 1200);

    // Fade out before transition
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, 2800);

    return () => {
      clearTimeout(logoTimer);
      clearTimeout(textTimer);
      clearTimeout(fadeTimer);
    };
  }, []);

  return (
    <div
      data-testid="splash-screen"
      className={`fixed inset-0 bg-white flex items-center justify-center transition-opacity duration-500 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-[600px] h-[600px] bg-orange-100/50 rounded-full blur-3xl -top-64 -left-64"></div>
        <div className="absolute w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-3xl -bottom-64 -right-64"></div>
      </div>

      <div className="text-center z-10">
        {/* Logo with pop-up and rotate animation */}
        <div className={`mb-8 transition-all duration-700 ease-out ${
          logoAnimated 
            ? 'scale-100 rotate-0 opacity-100' 
            : 'scale-0 rotate-180 opacity-0'
        }`}>
          <div className="relative inline-block">
            {/* Subtle shadow behind logo */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-200 to-blue-200 blur-2xl opacity-40 scale-125 rounded-full"></div>
            
            {/* Logo container */}
            <div className="relative">
              <img 
                src={SWAMI_LOGO} 
                alt="Swami Software" 
                className={`h-40 w-auto object-contain transition-transform duration-500 ${
                  logoAnimated ? 'animate-logo-settle' : ''
                }`}
              />
            </div>
          </div>
        </div>

        {/* RFQ Tracker text with slide-in animation */}
        <div className={`overflow-hidden h-14 ${showText ? 'opacity-100' : 'opacity-0'}`}>
          <h1 className={`text-5xl font-bold transition-all duration-700 ease-out ${
            showText ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
          }`}>
            <span className="bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 bg-clip-text text-transparent">
              RFQ
            </span>
            <span className="text-[#0A2540] ml-2">Tracker</span>
          </h1>
        </div>

        {/* Subtitle with fade in */}
        <p className={`text-gray-500 text-base mt-3 transition-all duration-500 delay-300 ${
          showText ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          Powered by Swami Software
        </p>

        {/* Loading indicator */}
        <div className={`mt-10 transition-all duration-500 ${showText ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center justify-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-orange-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes logo-settle {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        .animate-logo-settle {
          animation: logo-settle 0.5s ease-out forwards;
          animation-delay: 0.7s;
        }
      `}</style>
    </div>
  );
}
